import { useQuery } from '@tanstack/react-query';
import { getAccountRiskDetails } from '../../api';

/**
 * Fetches an account's risk details (`details[factor] = { min, max, company,
 * industry }`).
 *
 * Shares the canonical `['customer-risk-details', accountId]` cache key with
 * `useGetCustomerRiskFactors` — both read the same `getAccountRiskDetails`
 * endpoint, so they must not fork the cache into a second fetch.
 *
 * Does not surface an error toast: the Policy CF surfaces mount this hook more
 * than once for the same account (the details drawer gates its spinner on it
 * while the panel inside also reads it), and React Query fires `onError` per
 * observer, so a toast here would fire twice. Consumers render their own error
 * state from `isError` instead.
 */
export const useAccountRiskDetails = ({
  accountId,
}: {
  accountId?: string;
}) => {
  const query = useQuery(
    ['customer-risk-details', accountId],
    () => getAccountRiskDetails(accountId).then((res: any) => res.data),
    { enabled: !!accountId, refetchOnWindowFocus: false }
  );

  // React Query v4 keeps a disabled query (no accountId) in `isLoading: true`
  // indefinitely. Treat "nothing to fetch" as not-loading so consumers that
  // gate a spinner on `isLoading` render an empty state instead of hanging.
  return { ...query, isLoading: query.isLoading && !!accountId };
};

export default useAccountRiskDetails;
